import React from 'react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import * as mobx from 'mobx';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import classnames from 'classnames';
import DefaultButton from '../../../../global/default-button/default-button';

import './all-events-configuration-form.scss';
import { ICsvColumn } from '../../../../../api/connections.dao';
import { FormField } from '../../../../global/form/form-field/form-field';
import Text from '../../../../global/text/text';
import { ListLayout } from '../../../../global/tables/table-list-layout';
import { EventConfigurationModalItem } from './event-configuration-modal-item/event-configuration-modal-item';
import AllEventConfigurationVM from './all-events-configuration-modal.viewmodel';
import { DELETE_TEXT } from '../../../../../constants/common-labels.constants';
import { ProjectCSPNonceDerictives } from '../../../../../constants/common.constants';

interface IAllEventConfigurationPresentationProps {
    intl: IntlShape;
    vm: AllEventConfigurationVM;
}

const reorder = (list, startIndex, endIndex): ICsvColumn[] => {
    const result: ICsvColumn[] = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    return result;
};

const AllEventConfigurationPresentation = injectIntl(mobxReact.observer(({ vm, intl }: IAllEventConfigurationPresentationProps) => {
    const { allColumns } = vm;
    const [sortableFields, excludedFields] = allColumns.reduce((prev, next) => {
        prev[next.included ? 0 : 1].push(next);
        return prev;
    }, [[], []]);

    const toggleVisibility = mobx.action((header: string, currentColumn: ICsvColumn) => {
        const updatedColumn = { ...currentColumn, included: !currentColumn.included };

        const updatedColumns = updatedColumn.included ?
            sortableFields.concat(updatedColumn, excludedFields.filter(field => field.header !== header)) :
            allColumns.filter(field => field.header !== header).concat(updatedColumn);

        vm.updateOrder(updatedColumns);
    });

    const onDragEnd = mobx.action(result => {
        if (!result.destination) return;

        const sorted: ICsvColumn[] = reorder(
            sortableFields,
            result.source.index,
            result.destination.index
        );

        vm.updateOrder(sorted.concat(excludedFields));
    });

    return (

                <div className='oe-events-configuration h-100'>
                    <div className='h-100'>
                        <div className='flex oe-events-configuration_wrapper h-100'>
                            <div className='flex oe-events-configuration__configurations'>
                                <FormField label={
                                    <FormattedMessage
                                        defaultMessage='Save Current Configuration As'
                                        id='oe.users.saveConfiguration'
                                        description='Label text'
                                    />
                                }>
                                    <input
                                        className='oe-form-field__control'
                                        value={vm.configurationName}
                                        onChange={vm.setConfigurationName}
                                        onBlur={vm.setConfigurationName}
                                    />
                                    <span className='oe-form-field__error'>
                                        {vm.isConfigurationNameTouched && vm.getConfigurationNameError()}
                                    </span>
                                    <DefaultButton
                                        modifier={['slim', 'confirm-safe']}
                                        text='Save'
                                        className='m-top-10px m-bottom-15px m-left'
                                        onClick={vm.saveConfiguration}
                                    />
                                    <ListLayout>
                                        {vm.configurationTemplates.map(({connectionExportTemplateId, name}) => {
                                            const isSelected = vm.selectedConfigurationId === connectionExportTemplateId;
                                            return (
                                                <tr key={connectionExportTemplateId} className={classnames(
                                                    {
                                                        'fade-in': vm.addedConfigurationIds.has(connectionExportTemplateId),
                                                        'fade-out':  vm.removedConfigurationIds.has(connectionExportTemplateId),
                                                    }
                                                )} >
                                                    <td className={classnames('p-top-8px p-bottom-8px', {
                                                            'active': isSelected,
                                                            'hoverable': !isSelected
                                                        }
                                                    )}>
                                                        <div className='user-item__column'>
                                                            <Text
                                                                message={name}
                                                                withEllipsis
                                                                withHighlight
                                                            />
                                                            <div className='m-left oe-events-configuration__buttons-block'>
                                                                <DefaultButton
                                                                    text={
                                                                        <FormattedMessage
                                                                            id='oe.common.load'
                                                                            defaultMessage='Load'
                                                                            description='Label text'
                                                                        />
                                                                    }
                                                                    modifier={['slim', 'confirm-safe']}
                                                                    onClick={() => vm.loadConfiguration(connectionExportTemplateId)}
                                                                />
                                                                <DefaultButton
                                                                    text={
                                                                        <FormattedMessage {...DELETE_TEXT} />
                                                                    }
                                                                    modifier={['slim', 'cancel']}
                                                                    className='m-left-10px'
                                                                    onClick={() => vm.onDelete(connectionExportTemplateId)}
                                                                />
                                                            </div>
                                                        </div>

                                                    </td>
                                                </tr>
                                            );
                                        })}

                                    </ListLayout>
                                </FormField>
                            </div>
                            <div className='flex configuration-column__container'>
                            <DragDropContext onDragEnd={onDragEnd} nonce={ProjectCSPNonceDerictives.OEC_DRAG_DROP_STYLES}>
                                <Droppable droppableId='droppable'>
                                    {droppableProps => (
                                        <div
                                            {...droppableProps.droppableProps}
                                            ref={droppableProps.innerRef}
                                            className='w-100'
                                        >
                                            {sortableFields.map((field, index) => (
                                                <Draggable key={field.position} draggableId={field.position.toString()} index={index}>
                                                    {draggableProps => (
                                                            <div
                                                                className='configuration-column__wrapper'
                                                                ref={draggableProps.innerRef}
                                                                {...draggableProps.draggableProps}
                                                            >
                                                            <EventConfigurationModalItem
                                                                key={field.header}
                                                                intl={intl}
                                                                toggleVisibility={toggleVisibility}
                                                                item={field}
                                                            />
                                                            <div
                                                                className='configuration-column__drag-icon'
                                                                {...draggableProps.dragHandleProps}
                                                            >
                                                                <i {...draggableProps.dragHandleProps} tabIndex={-1}/>
                                                            </div>
                                                        </div>
                                                    )}
                                                </Draggable>
                                            ))}
                                        {droppableProps.placeholder}
                                    </div>
                                    )}
                                </Droppable>
                                {!!excludedFields.length &&
                                <>
                                    {excludedFields.map(item => (
                                        <EventConfigurationModalItem
                                            key={item.header}
                                            intl={intl}
                                            toggleVisibility={toggleVisibility}
                                            item={item}
                                        />
                                    ))}
                                </>}
                            </DragDropContext>
                            </div>
                        </div>

                    </div>
                </div>
    );
}));

export {
    AllEventConfigurationPresentation,
};
